import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  HostListener,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ShopSearchHelperService } from '../shop-search-helper.service';
import { BookingParameters } from '../../../entities/bookingParameters';
import { SkihirePoiInterface } from '../../../interfaces/skihire-poi-interface';
import { PoiService } from '../../../../shared/services/poi.service';
import { TownService } from '../../../../core/services/town.service';
import { ResolutionService } from '../../../../core/services/resolution.service';
import { SkihirePoiResponseInterface } from '../../../interfaces/skihire-poi-response-interface';
import { TranslateService } from '@ngx-translate/core';
import { ScrollService } from '../../../../core/services/scroll.service';
import { ShopDataService } from '../../../../shared/services/shop-data.service';
import { Shop } from '../../../../shared/entities/shop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  alarm,
  IconComponent,
  locationPin,
  place,
  provideIcons,
  shopPin,
  smMap,
  worldPin
} from '../../../icons';
import { PlatformassetPipe } from '../../../../../../projects/platform-manager/src/public_api';
import { AlpitranslatePipe } from '../../../../core/pipes/alpitranslate.pipe';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { DatePipe, NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'app-poi-search',
  templateUrl: './poi-search.component.html',
  styleUrls: ['./poi-search.component.scss'],
  providers: [provideIcons({ place, alarm, locationPin, shopPin, smMap, worldPin })],
  imports: [
    PlatformassetPipe,
    AlpitranslatePipe,
    HighlightPipe,
    ReactiveFormsModule,
    IconComponent,
    DatePipe,
    NgTemplateOutlet
  ]
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoiSearchComponent implements OnInit {
  @Input() poiFormGroup: UntypedFormGroup;
  @Input() townslug: string;
  @Input() bookingParameters: BookingParameters;
  @Input() isChrome: boolean;
  @Input() refererId: string;
  @Input() disableSearch: boolean;
  @Input() platform: string;

  selected = 0;

  /**
   * emits the new selected location as string
   */
  @Output() changedLocation: EventEmitter<SkihirePoiInterface> = new EventEmitter<SkihirePoiInterface>();

  /**
   * emits the initial location by slug
   */
  @Output() setLocation: EventEmitter<SkihirePoiInterface> = new EventEmitter<SkihirePoiInterface>();

  @ViewChild('searchFormDiv') searchFormDiv: ElementRef;
  @ViewChild('poiInput', { static: true }) poiInput: ElementRef;
  @ViewChild('poiSearch', { static: true }) poiSearch: ElementRef;

  get poiFormControl() {
    return this.poiFormGroup.controls.poi;
  }

  onPoiSearch = false;

  // observable result of pois from the api service
  private townSub: Subscription;

  pois: Array<SkihirePoiInterface> = [];
  similar: Array<SkihirePoiInterface> = [];
  locationResults: Array<SkihirePoiInterface> = [];
  shopResults: Array<SkihirePoiInterface> = [];
  regionResults: Array<SkihirePoiInterface> = [];
  countryResults: Array<SkihirePoiInterface> = [];
  displayResults = false; // used to show/hide dropdown results
  nowSearching = false; // used to show/hide the "now searching" line
  resultLimit = 7; // used to limit number of results displayed.
  allResults: Array<SkihirePoiInterface> = [];

  constructor(
    private poiService: PoiService,
    private townService: TownService,
    public shopSearchHelper: ShopSearchHelperService,
    private resolutionService: ResolutionService,
    public translateService: TranslateService,
    private scrollService: ScrollService,
    private shopDataService: ShopDataService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    if (this.refererId) {
      this.poiService.setReferer(this.refererId);
    }
    this.poiService.onPoiChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pois: SkihirePoiResponseInterface) => {
        this.pois = pois.strict;
        this.similar = pois.similar;
        this.prepareResults(this.pois, this.similar);
      });
    this.poiService.onSearching
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nowSearching: boolean) => {
        this.nowSearching = nowSearching;
      });
    if (this.poiService.getLastResult()) {
      this.pois = this.poiService.getLastResult().strict;
      this.similar = this.poiService.getLastResult().similar;
      this.prepareResults(this.pois, this.similar);
    }
    if (this.bookingParameters.poi && this.bookingParameters.poi.type === 'AlpinShop') {
      this.poiFormControl.setValue(this.bookingParameters.poi.name);
      this.poiService.updatePhrase(this.bookingParameters.poi.name);
    } else if (this.townslug !== null && this.townslug !== undefined && this.townslug !== '') {
      // update town name according to current slug.
      this.updateSlug(this.townslug);
    }

    this.shopSearchHelper.newBookingParameters.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      if (this.bookingParameters.poi && this.bookingParameters.poi.type === 'AlpinShop') {
        this.poiFormControl.setValue(this.bookingParameters.poi.name);
        this.poiService.updatePhrase(this.bookingParameters.poi.name);
      } else if (data.slug !== null && data.slug !== undefined && data.slug !== '') {
        this.townslug = data.slug;
        this.updateSlug(this.townslug);
      }
    });

    // if you're on a partner page OR the search is set to be disabled, let angular know.
    // html attribute "disabled" is not in sync otherwise and will not work correctly anymore
    if (
      this.disableSearch === true ||
      (this.bookingParameters.partner && !this.bookingParameters.partner.endsWith('bergfex'))
    ) {
      this.poiFormControl.disable();
    }
  }

  private prepareResults(strict: Array<SkihirePoiInterface>, similar: Array<SkihirePoiInterface>) {
    this.countryResults = this.mixNMatchResults(strict, similar, ['AlpinCountry'], 3);
    this.locationResults = this.mixNMatchResults(strict, similar, ['AlpinTown', 'PointOfInterest'], 5);
    this.shopResults = this.mixNMatchResults(
      strict,
      similar,
      ['AlpinShop'],
      this.locationResults.length === 0 ? 13 : this.locationResults.length > 4 ? 3 : 6
    );
    this.regionResults = this.mixNMatchResults(
      strict,
      similar,
      ['AlpinRegion', 'AlpinResort', 'AlpinProvince'],
      this.locationResults.length > 4 ? 3 : 6
    );
    this.locationResults = this.mixNMatchResults(
      strict,
      similar,
      ['AlpinTown', 'PointOfInterest'],
      14 - (this.shopResults.length + this.regionResults.length + this.countryResults.length)
    );
    this.allResults = [
      ...this.locationResults,
      ...this.shopResults,
      ...this.regionResults,
      ...this.countryResults
    ];
    this.cdr.detectChanges();
  }

  private filterResults(res: Array<SkihirePoiInterface>, types: Array<string>): Array<SkihirePoiInterface> {
    return res.filter(_res => types.indexOf(_res.type) !== -1);
  }

  private mixNMatchResults(
    strict: Array<SkihirePoiInterface>,
    similar: Array<SkihirePoiInterface>,
    types: Array<string>,
    numberResults = 4
  ): Array<SkihirePoiInterface> {
    const strictResults = this.filterResults(strict, types).slice(0, numberResults);
    const simResults = this.filterResults(similar, types);
    if (strictResults.length < numberResults && simResults.length) {
      return strictResults.concat(simResults.slice(0, numberResults - strictResults.length));
    }
    return strictResults;
  }

  updateSlug(slug: string) {
    if (slug.split('/').length > 3) {
      // must be a shop
      this.shopDataService.onLoadLocationBySlug
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((shop: Shop) => {
          this.poiFormControl.setValue(shop.name);
          this.poiService.updatePhrase(shop.name);
          this.setLocation.emit({
            url: '/de/skiverleih/' + slug,
            type: 'AlpinShop',
            foreign_id: +shop.id,
            name: shop.name
          });
        });
      this.shopDataService.getLocationBySlug(slug);
    } else {
      this.townService.onTownUpdate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(town => {
        this.poiFormControl.setValue(town.name);
        this.poiService.updatePhrase(town.name);
        if (slug.split('/').length > 1) {
          this.setLocation.emit({
            url: '/de/skiveleih/' + slug,
            type: 'AlpinTown',
            name: town.name
          });
        } else {
          // Pois are handled like towns except their slug is only one part long
          this.setLocation.emit({
            url: '/de/skiveleih/' + slug,
            type: 'PointOfInterest',
            name: town.name
          });
        }
        if (this.townSub) {
          this.townSub.unsubscribe();
        }
      });
      this.townService.getTownForSlug(slug);
    }
  }

  searchPoi(phrase: string) {
    this.displayResults = true;
    this.cdr.detectChanges();
    if (this.poiFormControl.value !== '') {
      this.poiService.updatePhrase(phrase);
    }
  }

  selectPoi(poi: SkihirePoiInterface) {
    this.poiFormControl.setValue(poi.name);
    this.poiFormControl.markAsDirty();
    if (['AlpinShop', 'AlpinTown', 'PointOfInterest'].includes(poi.type)) {
      this.changedLocation.emit(poi);
    } else {
      window.location.href = poi.url;
      // follow link
    }
    this.poiInput.nativeElement.blur();
    this.hideResults();
  }

  /**
   * hides the search dropdown
   */
  hideResults() {
    this.displayResults = false;
    if (this.bookingParameters.poi) {
      this.poiFormControl.setValue(this.bookingParameters.poi.name);
    } else {
      this.poiFormControl.setValue(''); // <- our serach page is no longer working, don't redirect
    }
    this.cdr.detectChanges();
  }

  /**
   * shows the search dropdown
   */
  showResults() {
    if (this.resolutionService.isMobileDevice()) {
      this.setFocus({ behavior: 'smooth' });
    }
    if (this.poiFormControl.value !== '' && !this.shopSearchHelper.formMinified) {
      this.displayResults = true;
      this.cdr.detectChanges();
    }
  }

  public setFocus(scrollIntoViewOptions?: ScrollIntoViewOptions) {
    try {
      if (scrollIntoViewOptions) {
        this.scrollService.scrollIntoView(this.poiInput.nativeElement);
      }
      this.poiInput.nativeElement.focus();
    } catch {
      console.error('could not focus on search input');
    }
  }

  @HostListener('document:click', ['$event.target'])
  onPageClick(target: ElementRef) {
    if (this.displayResults) {
      // picker is open. if click target not on form or picker, close it
      if (!this.poiSearch.nativeElement.contains(target)) {
        // we need to hide the selectbox
        this.hideResults();
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event) {
    if (this.onPoiSearch) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selected += 1;
          // if (this.selected >= this.pois.slice(0, this.resultLimit).length + this.similar.slice(0, this.resultLimit).length) {
          if (this.selected >= this.allResults.length) {
            this.selected = 0;
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selected -= 1;
          if (this.selected < 0) {
            // this.selected = this.pois.slice(0, this.resultLimit).length + this.similar.slice(0, this.resultLimit).length - 1;
            this.selected = this.allResults.length - 1;
          }
          break;
        case 'Enter':
          // if (this.selected < this.pois.slice(0, this.resultLimit).length) {
          //   this.selectPoi(this.pois[this.selected]);
          // } else {
          //   this.selectPoi(this.similar.slice(0, this.resultLimit)[this.selected - this.pois.slice(0, this.resultLimit).length]);
          // }
          this.selectPoi(this.allResults[this.selected]);
          event.preventDefault();
          break;
        case 'Tab':
          this.hideResults();
          break;
        case 'Escape':
          this.hideResults();
          break;
      }
    }
  }
}
